import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGithub,
  faLinkedin,
  faInstagram,
} from '@fortawesome/free-brands-svg-icons';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';

const ContactSection = styled.section`
  padding: 6rem 2rem;
  background: ${(props) => props.theme.colors.secondary}30;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
`;

const Title = styled(motion.h2)`
  font-size: 2.5rem;
  color: ${(props) => props.theme.colors.secondary};
  text-align: center;
  margin-bottom: 1rem;
`;

const Subtitle = styled(motion.p)`
  text-align: center;
  color: ${(props) => props.theme.colors.gray.dark};
  margin-bottom: 3rem;
  font-size: 1.1rem;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContactForm = styled(motion.form)`
  background: ${(props) => props.theme.colors.white};
  padding: 2.5rem;
  border-radius: 15px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  position: relative;
`;

const HoneypotField = styled.div`
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${(props) => props.theme.colors.secondary};
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem;
  background: ${(props) => props.theme.colors.gray.lightest};
  border: 2px solid ${(props) => props.theme.colors.gray.light};
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.primary}20;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem;
  background: ${(props) => props.theme.colors.gray.lightest};
  border: 2px solid ${(props) => props.theme.colors.gray.light};
  border-radius: 8px;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.primary}20;
  }
`;

const SubmitButton = styled(motion.button)`
  background: ${(props) => props.theme.colors.secondary};
  color: ${(props) => props.theme.colors.white};
  padding: 1rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const InfoTitle = styled.h3`
  font-size: 1.5rem;
  color: ${(props) => props.theme.colors.secondary};
  margin-bottom: 1.5rem;
`;

const InfoText = styled.p`
  color: ${(props) => props.theme.colors.gray.dark};
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const SocialLink = styled(motion.a)`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: ${(props) => props.theme.colors.secondary};
  color: ${(props) => props.theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => props.theme.colors.primary};
    transform: translateY(-3px);
  }
`;

const StatusMessage = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: ${(props) => props.theme.colors.white};
  color: ${(props) => props.theme.colors.black};
  padding: 1rem 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  z-index: 1002;
  font-size: 1rem;
  text-align: center;
`;

const Contact: React.FC = () => {
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [formStarted] = useState(() => Math.floor(Date.now() / 1000));
  const formRef = useRef<HTMLFormElement | null>(null); // Add a ref for the form

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    try {
      const response = await fetch('contact.php', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        setStatusMessage(result.message);
        formRef.current?.reset();
      } else {
        setStatusMessage(result.message);
      }

      setTimeout(() => setStatusMessage(null), 6000);
    } catch (error) {
      console.error('Error occurred:', error);
      setStatusMessage('An error occurred. Please try again later.');
      setTimeout(() => setStatusMessage(null), 6000);
    }
  };

  return (
    <ContactSection id='contact'>
      <Container>
        <Title
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Drop a Thought
        </Title>
        <Subtitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          Wanna collaborate and create something together, or just say hi?
        </Subtitle>

        {statusMessage && <StatusMessage>{statusMessage}</StatusMessage>}

        <ContactGrid>
          <ContactForm
            ref={formRef} // Attach the ref to the form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <HoneypotField aria-hidden='true'>
              <input
                type='text'
                name='website'
                tabIndex={-1}
                autoComplete='off'
              />
            </HoneypotField>
            <input type='hidden' name='form_started' value={formStarted} />
            <FormGroup>
              <Label htmlFor='name'>Name</Label>
              <Input type='text' id='name' name='name' required />
            </FormGroup>
            <FormGroup>
              <Label htmlFor='email'>Email</Label>
              <Input type='email' id='email' name='email' required />
            </FormGroup>
            <FormGroup>
              <Label htmlFor='message'>Message</Label>
              <TextArea id='message' name='message' required />
            </FormGroup>
            <SubmitButton
              type='submit'
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Send Message <FontAwesomeIcon icon={faPaperPlane} />
            </SubmitButton>
          </ContactForm>

          <ContactInfo>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <InfoTitle>Let's Connect</InfoTitle>
              <InfoText>
                Got a wild idea? A weird question? A beautiful contradiction to
                unravel? Whether you want to collaborate, brainstorm, or just
                throw philosophical tomatoes at the void — I’m in. Use the form
                to reach out, or find me on the following platforms:
              </InfoText>
              <SocialLinks>
                <SocialLink
                  href='https://github.com/zaaraa-z'
                  target='_blank'
                  rel='noopener noreferrer'
                  whileHover={{ scale: 1.1 }}
                >
                  <FontAwesomeIcon icon={faGithub} />
                </SocialLink>
                <SocialLink
                  href='https://linkedin.com/in/zahra-zamanian'
                  target='_blank'
                  rel='noopener noreferrer'
                  whileHover={{ scale: 1.1 }}
                >
                  <FontAwesomeIcon icon={faLinkedin} />
                </SocialLink>
                <SocialLink
                  href='https://www.instagram.com/zara.zmn/'
                  target='_blank'
                  rel='noopener noreferrer'
                  whileHover={{ scale: 1.1 }}
                >
                  <FontAwesomeIcon icon={faInstagram} />
                </SocialLink>
              </SocialLinks>
            </motion.div>
          </ContactInfo>
        </ContactGrid>
      </Container>
    </ContactSection>
  );
};

export default Contact;
